import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SURVEY_URL } from '@insite-store360/shared';
import { catchError, map, Observable } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

// ─── Response DTOs ────────────────────────────────────────────────────────────

/**
 * One KPI card as returned by GET /execDashboard/kpiCards: monthly and full-year values from
 * exec_dashboard_kpi summed for one Business Call. Amounts are in M, ratios in %.
 */
export interface KpiCardDto {
  /** Full DB value-type name, e.g. "Net Sales PY" */
  valueTypeName: string;
  /** Display label (PY suffix stripped), e.g. "Net Sales" */
  label: string;
  /** Year and Business Call of the values (latest entered when not requested) */
  year: number | null;
  bcNo: number | null;
  /** Last month in the YTD sum */
  month: number | null;

  ytdActual: number | null;
  ytdBu: number | null;
  ytdAchvBu: number | null;
  ytdGrPy: number | null;

  fyBc: number | null;
  fyBu: number | null;
  fyAchvBu: number | null;
  fyGrPy: number | null;
}

// ─── Filter params ────────────────────────────────────────────────────────────

export interface KpiCardsParams {
  /** Fiscal year e.g. 2026 — null means latest entered */
  year: number | null;
  /** Business Call 1-12 — null means latest entered */
  bcMonth?: number | null;
  /** YTD start month — null means January */
  fromMonth?: number | null;
  /** YTD end month 1-12 — null means the latest month with an actual */
  ytdMonth: number | null;
  market?: string;
  channel?: string;
  commercialArea?: string;
  subCommercialArea?: string;
  /** Currency code for amounts; omit for EUR */
  currency?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class ExecDashboardService {
  private readonly BASE = `${SURVEY_URL}/execDashboard`;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService,
  ) { }

  // ── KPI Cards ───────────────────────────────────────────────────────────────

  /**
   * Fetch aggregated KPI card data (Net Sales, CTF, EBIT) for a given
   * fiscal year, sliced to YTD through ytdMonth, with optional
   * market / channel / commercial-area filters.
   *
   * Maps to GET /execDashboardUpload/kpiCards
   */
  getKpiCards(params: KpiCardsParams): Observable<KpiCardDto[]> {
    let queryParams = new HttpParams();
    // Only add year / ytdMonth when not "All" (null)
    if (params.year !== null) queryParams = queryParams.set('year', String(params.year));
    if (params.ytdMonth !== null) queryParams = queryParams.set('ytdMonth', String(params.ytdMonth));
    if (params.fromMonth != null) queryParams = queryParams.set('fromMonth', String(params.fromMonth));
    if (params.bcMonth != null) queryParams = queryParams.set('bcMonth', String(params.bcMonth));

    if (params.market) queryParams = queryParams.set('market', params.market);
    if (params.channel) queryParams = queryParams.set('channel', params.channel);
    if (params.commercialArea) queryParams = queryParams.set('commercialArea', params.commercialArea);
    if (params.subCommercialArea) queryParams = queryParams.set('subCommercialArea', params.subCommercialArea);
    if (params.currency) queryParams = queryParams.set('currency', params.currency);

    return this.http
      .get<{ result: KpiCardDto[] } | KpiCardDto[]>(`${this.BASE}/kpiCards`, { params: queryParams })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── SAP P&L (Databricks) ──────────────────────────────────────────────────────

  /** Maps to GET /execDashboard/sapPnl */
  getSapPnlData(year?: number | null, month?: number | null): Observable<Record<string, unknown>[]> {
    let queryParams = new HttpParams();
    if (year != null) queryParams = queryParams.set('year', String(year));
    if (year != null && month != null) queryParams = queryParams.set('month', String(month));

    return this.http
      .get<any>(`${this.BASE}/sapPnl`, { params: queryParams })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Currencies ─────────────────────────────────────────────────────────────────────────

  /** EUR plus every currency with an EUR exchange rate. Maps to GET /execDashboard/currencies */
  getCurrencies(): Observable<string[]> {
    return this.http
      .get<any>(`${this.BASE}/currencies`)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Value Types ─────────────────────────────────────────────────────────────

  /**
   * Fetch all active value types (Net Sales PY, CTF PY, EBIT PY).
   * Maps to GET /execDashboardUpload/valueTypes
   */
  getValueTypes(): Observable<{ id: number; name: string }[]> {
    return this.http
      .get<any>(`${this.BASE}/valueTypes`)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Filter Options ──────────────────────────────────────────────────────────

  /**
   * Fetch distinct years, plus the months and Business Calls uploaded for the given year.
   * Used to populate filter dropdowns.
   *
   * Maps to GET /execDashboard/filterOptions
   */
  getFilterOptions(
    valueTypeId?: number,
    year?: number,
  ): Observable<{ years: number[]; months: number[]; bcs: number[] }> {
    let queryParams = new HttpParams();
    if (valueTypeId) queryParams = queryParams.set('valueTypeId', String(valueTypeId));
    if (year) queryParams = queryParams.set('year', String(year));

    return this.http
      .get<any>(`${this.BASE}/filterOptions`, { params: queryParams })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Region / Country Options ────────────────────────────────────────────────

  /** Maps to GET /execDashboard/regions */
  getRegions(): Observable<string[]> {
    return this.http
      .get<any>(`${this.BASE}/regions`)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  /**
   * Maps to GET /execDashboard/countries
   * @param regions region names; empty / omitted returns countries of all regions
   */
  getCountries(regions: string[] = []): Observable<ExecRegionCountryDto[]> {
    let queryParams = new HttpParams();
    if (regions.length) queryParams = queryParams.set('region', regions.join(','));

    return this.http
      .get<any>(`${this.BASE}/countries`, { params: queryParams })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  /** Category / Subcategory (Global Product Line) pairs. Maps to GET /execDashboard/categories */
  getCategories(): Observable<ExecCategoryProductLineDto[]> {
    return this.http
      .get<any>(`${this.BASE}/categories`)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Portfolio Slice (Heatmap Matrix) ────────────────────────────────────────

  /**
   * Fetch portfolio slice matrix (Market × Category / Brand / Channel).
   * Maps to GET /execDashboard/slice
   */
  getPortfolioSlice(params: PortfolioSliceParams): Observable<PortfolioSliceResult> {
    let queryParams = new HttpParams();
    if (params.sliceType) queryParams = queryParams.set('sliceType', params.sliceType);
    if (params.year != null) queryParams = queryParams.set('year', String(params.year));
    if (params.ytdMonth != null) queryParams = queryParams.set('ytdMonth', String(params.ytdMonth));
    if (params.fromMonth != null) queryParams = queryParams.set('fromMonth', String(params.fromMonth));
    if (params.market) queryParams = queryParams.set('market', params.market);
    if (params.channel) queryParams = queryParams.set('channel', params.channel);
    if (params.commercialArea) queryParams = queryParams.set('commercialArea', params.commercialArea);
    if (params.subCommercialArea) queryParams = queryParams.set('subCommercialArea', params.subCommercialArea);
    if (params.category) queryParams = queryParams.set('category', params.category);
    if (params.productLine) queryParams = queryParams.set('productLine', params.productLine);
    if (params.bcMonth != null) queryParams = queryParams.set('bcMonth', String(params.bcMonth));
    if (params.currency) queryParams = queryParams.set('currency', params.currency);

    return this.http
      .get<{ result: PortfolioSliceResult } | PortfolioSliceResult>(`${this.BASE}/slice`, { params: queryParams })
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  // ── Monthly Actuals vs BU vs PY ──────────────────────────────────────────────

  /**
   * Fetch monthly actuals vs BU vs PY.
   *
   * Returns one MonthlyActualsSeriesDto per value type (Net Sales, CTF, EBIT).
   * Each series has 12 points (months 1-12); months with no uploaded data return zeros.
   *
   * Maps to GET /execDashboard/monthlyActuals
   */
  getMonthlyActuals(params: MonthlyActualsParams = {}): Observable<MonthlyActualsSeriesDto[]> {
    let queryParams = new HttpParams();
    if (params.year != null) queryParams = queryParams.set('year', String(params.year));
    if (params.market) queryParams = queryParams.set('market', params.market);
    if (params.channel) queryParams = queryParams.set('channel', params.channel);
    if (params.category) queryParams = queryParams.set('category', params.category);
    if (params.productLine) queryParams = queryParams.set('productLine', params.productLine);
    if (params.brand) queryParams = queryParams.set('brand', params.brand);
    if (params.commercialArea) queryParams = queryParams.set('commercialArea', params.commercialArea);
    if (params.subCommercialArea) queryParams = queryParams.set('subCommercialArea', params.subCommercialArea);
    if (params.bcMonth != null) queryParams = queryParams.set('bcMonth', String(params.bcMonth));
    if (params.currency) queryParams = queryParams.set('currency', params.currency);

    return this.http
      .get<{ result: MonthlyActualsSeriesDto[] } | MonthlyActualsSeriesDto[]>(
        `${this.BASE}/monthlyActuals`,
        { params: queryParams },
      )
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }
}

// ─── Region / Country Types ──────────────────────────────────────────────────

export interface ExecRegionCountryDto {
  /** sub_commercial_area, e.g. "NEA" */
  region: string;
  /** market, e.g. "China" */
  country: string;
}

export interface ExecCategoryProductLineDto {
  /** product_line_category_name, e.g. "Care" */
  category: string;
  /** Global Product Line shown as the subcategory */
  productLine: string;
  /** product_line_category.is_visible: selected by default in the Category filter */
  isVisible: boolean;
}

// ─── Portfolio Slice Types ───────────────────────────────────────────────────

export interface PortfolioSliceParams {
  sliceType?: 'category' | 'brand' | 'channel';
  year?: number | null;
  /** Start of the month range ending at ytdMonth */
  fromMonth?: number | null;
  ytdMonth?: number | null;
  market?: string;
  channel?: string;
  commercialArea?: string;
  subCommercialArea?: string;
  /** Comma-separated product-line category names */
  category?: string;
  /** Comma-separated Global Product Lines (subcategories) */
  productLine?: string;
  /** Business Call 1–12 (reads that BC's uploaded files); omit for AC (latest BC of each year) */
  bcMonth?: number | null;
  /** Currency code for amounts; omit for EUR */
  currency?: string;
}

export interface PortfolioSliceCell {
  nsAbs: number;
  ctfAbs: number;
  ebitAbs: number;
  nsPct: number;
  ctfPct: number;
  mustWin?: boolean;
}

export interface PortfolioSliceRow {
  market: string;
  cells: Record<string, PortfolioSliceCell>;
}

export interface PortfolioSliceResult {
  sliceType: string;
  columns: string[];
  rows: PortfolioSliceRow[];
  grandTotal: {
    nsAbs: number;
    ctfAbs: number;
    ebitAbs: number;
    ctfPct: number;
  };
  columnTotals: Record<string, {
    nsAbs: number;
    ctfAbs: number;
    ebitAbs: number;
    ctfPct: number;
  }>;
}

// ─── Monthly Actuals Types ────────────────────────────────────────────────────

/** Optional filter params for GET /execDashboard/monthlyActuals */
export interface MonthlyActualsParams {
  /** Fiscal year e.g. 2026 — null / omit for All years */
  year?: number | null;
  /** Comma-separated market names */
  market?: string;
  /** Comma-separated channel values (used when slice = 'channel') */
  channel?: string;
  /** Comma-separated product-line category names (used when slice = 'category') */
  category?: string;
  /** Comma-separated Global Product Lines (subcategories) */
  productLine?: string;
  /** Comma-separated brand_desc values (used when slice = 'brand') */
  brand?: string;
  /** Comma-separated commercial area values */
  commercialArea?: string;
  /** Comma-separated sub-commercial area values */
  subCommercialArea?: string;
  /** Business Call 1–12 (reads that BC's uploaded files); omit for AC (latest BC of each year) */
  bcMonth?: number | null;
  /** Currency code for amounts; omit for EUR */
  currency?: string;
}

/** One month's data-point inside a MonthlyActualsSeriesDto. */
export interface MonthlyActualsPointDto {
  /** Calendar month 1-12 */
  month: number;
  /** Sum of the uploaded bc column (actual for closed months, forecast for open ones) */
  actual: number;
  /** Sum of the uploaded bu column (target) */
  bu: number;
  /** Sum of the uploaded value column (PY) */
  bc: number;
  /** actual / bu x 100  (0 when bu = 0) */
  achvVsBu: number;
  /** (actual / PY - 1) x 100  (0 when PY = 0) */
  growthVsBc: number;
}

/** Monthly series for one value type (Net Sales, CTF, EBIT). */
export interface MonthlyActualsSeriesDto {
  /** Full DB value-type name, e.g. "Net Sales PY" */
  valueTypeName: string;
  /** Display label (PY suffix stripped), e.g. "Net Sales" */
  label: string;
  /** 12 data-points, one per calendar month (months with no data = zeros) */
  points: MonthlyActualsPointDto[];
}
